import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useTenantDirectory } from "@/api/tenants";

/**
 * Route guard for a screen switched on per tenant (MPPI-00642).
 *
 * Same shape as `RequirePlatform`, and for the same reason: gating the ROUTE
 * means the lazy chunk is never fetched, so a tenant without the feature does
 * not download the page at all. `RequirePermission` checks permissions and
 * never scope or features, so on its own it would render the page
 * client-side for anyone holding the role (CLAUDE.md pattern 39).
 *
 * Renders nothing while the answer is loading — showing the page and
 * redirecting afterwards fetches the chunk anyway, which is the thing being
 * prevented.
 */
export function RequireFeature({
  feature,
  children,
}: {
  feature: string;
  children: ReactNode;
}) {
  const { hasFeature, featuresLoading } = useTenantDirectory();
  if (featuresLoading) return null;
  if (!hasFeature(feature)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** For hiding a menu item or a button inside a page that is already allowed. */
export function useHasFeature(feature: string): boolean {
  return useTenantDirectory().hasFeature(feature);
}
